import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/**
 * Phase 5.8 — KitReconcileDialog
 *
 * Dispatcher physical-count workflow:
 *   1. Open dialog → fetch /api/kits/{driver_id}/reconciliation
 *      (returns one row per kit line with `qty_expected`)
 *   2. Dispatcher types `qty_counted` per row (defaults to expected)
 *   3. SUBMIT → POST /api/kits/{driver_id}/correct with the diffs only
 *      Backend writes one `kit_correction` movement per mismatched line.
 *
 * Open the dialog by passing a non-null `kit` prop ({id, driver_id, driver_name}).
 * onClose() should setState back to null.
 */
export function KitReconcileDialog({ kit, onClose, onApplied }) {
  const [lines, setLines] = useState([]); // [{product_id, name, qty_expected, qty_counted}]
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!kit?.driver_id) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/kits/${kit.driver_id}/reconciliation`, { withCredentials: true })
      .then(({ data }) => {
        setLines((data.lines || []).map((l) => ({ ...l, qty_counted: l.qty_expected })));
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load reconciliation');
        onClose();
      })
      .finally(() => setLoading(false));
  }, [kit, onClose]);

  const diffs = lines.filter((l) => Number(l.qty_counted) !== Number(l.qty_expected));

  const handleSubmit = async () => {
    if (diffs.length === 0) {
      toast.success('Kit already matches — nothing to correct.');
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/kits/${kit.driver_id}/correct`,
        {
          counts: lines.map((l) => ({ product_id: l.product_id, qty_counted: Number(l.qty_counted) })),
          reason: reason || null,
        },
        { withCredentials: true },
      );
      toast.success(`Applied ${data.applied} correction${data.applied === 1 ? '' : 's'}`);
      onApplied?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply correction');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!kit} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0C0612] border border-[#FF9F0A] rounded-none max-w-2xl" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#FF9F0A] font-mono uppercase tracking-wider">
            📋 RECONCILE KIT · {kit?.driver_name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-[#A3C7A8] font-mono">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading kit contents…
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-[#A3C7A8] font-mono text-xs">
              {'//'} Count each item in the truck. Type what you actually see in the COUNTED column.
              We&apos;ll log every mismatch as a <span className="text-[#FF9F0A]">kit_correction</span> movement.
            </p>

            {/* Header */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#050003] border border-[#2A0F3D] text-[#6B8E73] font-mono text-[10px] uppercase tracking-wider">
              <div className="col-span-6">Product</div>
              <div className="col-span-2 text-right">Expected</div>
              <div className="col-span-2 text-right">Counted</div>
              <div className="col-span-2 text-right">Δ</div>
            </div>

            {/* Rows */}
            <div className="max-h-[40vh] overflow-y-auto space-y-1">
              {lines.map((l) => {
                const delta = Number(l.qty_counted) - Number(l.qty_expected);
                return (
                  <div
                    key={l.product_id}
                    className={`grid grid-cols-12 gap-2 px-3 py-2 items-center border ${
                      delta !== 0 ? 'border-[#FF9F0A] bg-[rgba(255,159,10,0.04)]' : 'border-[#2A0F3D]'
                    }`}
                    data-testid={`reconcile-row-${l.product_id}`}
                  >
                    <div className="col-span-6 text-[#39FF14] font-mono text-sm truncate">{l.name}</div>
                    <div className="col-span-2 text-right text-[#A3C7A8] font-mono text-sm">{l.qty_expected}</div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min={0}
                        value={l.qty_counted}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((p) =>
                              p.product_id === l.product_id ? { ...p, qty_counted: e.target.value } : p,
                            ),
                          )
                        }
                        className="h-7 bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none text-right text-sm"
                        data-testid={`counted-input-${l.product_id}`}
                      />
                    </div>
                    <div
                      className={`col-span-2 text-right font-mono text-sm ${
                        delta > 0 ? 'text-[#39FF14]' : delta < 0 ? 'text-[#FF3B30]' : 'text-[#4B6B52]'
                      }`}
                    >
                      {delta > 0 ? `+${delta}` : delta}
                    </div>
                  </div>
                );
              })}
              {lines.length === 0 && (
                <p className="py-6 text-center text-[#4B6B52] font-mono text-sm">
                  {'//'} This driver has no active kit.
                </p>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-1">
              <p className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-wider">// Reason (optional)</p>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. End-of-shift physical count · spillage · damaged jar"
                maxLength={200}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] rounded-none"
                data-testid="reconcile-reason"
              />
            </div>

            {/* Summary + actions */}
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#2A0F3D]">
              <p className="font-mono text-xs uppercase tracking-wider">
                {diffs.length === 0 ? (
                  <span className="text-[#39FF14] flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> No mismatches
                  </span>
                ) : (
                  <span className="text-[#FF9F0A]">{diffs.length} mismatch{diffs.length === 1 ? '' : 'es'} to log</span>
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono"
                  data-testid="reconcile-cancel"
                >
                  CANCEL
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || lines.length === 0}
                  className="bg-[#FF9F0A] text-[#050003] font-bold hover:bg-[#FFB13A] rounded-none font-mono"
                  data-testid="reconcile-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'APPLY CORRECTION'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
